import { Types } from "mongoose";
import { z, ZodError } from "zod";
import { isValid, parseISO, toDate } from "date-fns";

const ObjectId = Types.ObjectId;

/** string -> ObjectId */
export function zObjectId() {
  return z
    .string()
    .refine((input) => ObjectId.isValid(input), "Invalid ObjectId")
    .transform((input) => new ObjectId(input));
}

/** string -> Date */
export function zDate() {
  return z
    .string()
    .refine((input) => isValid(parseISO(input)), "Date is not valid ISOString")
    .transform((val) => toDate(parseISO(val)));
}

/** ObjectId -> string */
export function zStringId() {
  return z
    .union([z.string(), z.instanceof(ObjectId)])
    .refine((value) => ObjectId.isValid(value), {
      message: "Invalid ObjectId",
    })
    .transform((value) => String(value));
}

/** Date -> string */
export function zStringDate() {
  return z.date().transform((value) => {
    return value.toISOString();
  });
}

export function zTimestamps() {
  return {
    createdAt: zStringDate(),
    updatedAt: zStringDate(),
  };
}

export function zQueryArray(zodObject: z.AnyZodObject) {
  return z
    .string()
    .transform((value, ctx) => {
      const cartItemSchema = zodObject;

      try {
        const cartItemsObject = JSON.parse(value);
        return cartItemSchema.parse(cartItemsObject);
      } catch (error) {
        if (error instanceof ZodError) {
          error.errors.forEach((issue) => {
            ctx.addIssue({ ...issue, path: [issue.path[0]] });
          });
        } else {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid cartItems",
          });
        }

        return z.NEVER;
      }
    })
    .array();
}

export function zodPagination() {
  return {
    limit: z.coerce.number().int().nonnegative().max(99).optional().default(10),
    page: z.coerce.number().int().nonnegative().optional().default(0),
  };
}

export function isValidMongoObjectId(id: any) {
  return ObjectId.isValid(id);
}

export function zUsername() {
  return z
    .string()
    .min(3)
    .max(20)
    .regex(/^[a-zA-Z0-9_]+$/);
}

export default {
  isValidMongoObjectId,
};
